import { useState } from "react";
import { Loader2, LayoutTemplate } from "lucide-react";
import { api } from "@/lib/api";
import { CoverCompare } from "@/components/CoverCompare";

export const CoverDesigns = ({ book, onApply }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    setPicked(null);
    try {
      setData(await api.coverDesigns(book.id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create designs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t border-gold-500/10 pt-4 space-y-3" data-testid="cover-designs">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-ui text-sm font-semibold text-slate-100">New design, same artwork</p>
          <p className="font-ui text-xs text-slate-500">Different layout and type. No new art, no credits.</p>
        </div>
        <button data-testid="cover-designs-load" onClick={load} disabled={loading}
          className="flex items-center gap-2 font-ui text-sm font-medium border border-gold-400/50 text-gold-300 hover:bg-gold-400/10 disabled:opacity-40 rounded-lg px-3 py-2 transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LayoutTemplate className="w-4 h-4" />} Show designs
        </button>
      </div>
      {error && <p className="font-ui text-xs text-red-300" data-testid="cover-designs-error">{error}</p>}
      {data && (
        <>
          <p className="font-ui text-xs text-slate-500" data-testid="cover-designs-current">Current: {data.current}</p>
          <div className={`grid gap-3 ${data.designs.length === 1 ? "grid-cols-1 max-w-[50%] mx-auto" : "grid-cols-2"}`}>
            {data.designs.map((d) => (
              <button key={d.index} data-testid={`cover-design-option-${d.index}`} onClick={() => setPicked(d)}
                className={`text-left rounded-lg overflow-hidden border-2 transition-colors bg-navy-950/40 ${picked?.index === d.index ? "border-gold-400" : "border-transparent hover:border-gold-400/70"}`}>
                <img src={d.preview} alt={d.label} className="w-full aspect-[2/3] object-contain bg-navy-950" />
                <span className="block px-2 py-1.5 font-ui text-xs text-slate-300">{d.label}</span>
              </button>
            ))}
          </div>
          {data.mismatch && (
            <p data-testid="cover-designs-mismatch" className="font-ui text-xs text-amber-200">Heads-up: {data.mismatch}. These layouts reuse the current artwork.</p>
          )}
          {picked && (
            <CoverCompare book={book} candidate={{ label: picked.label, src: picked.preview }} actionLabel="Use this design"
              onCancel={() => setPicked(null)} onConfirm={() => onApply(picked.index)} />
          )}
          <p className="font-ui text-xs text-slate-500">Your current cover is saved under Saved covers, so you can always switch back.</p>
          {!data.designs.length && <p className="font-ui text-xs text-slate-400">No other design passes the quality check on this artwork. Try new artwork above.</p>}
        </>
      )}
    </div>
  );
};
